/**
 * MetronomeToggle Component
 * Metronome toggle button with volume slider on hover
 */

import React, { useState, useRef } from 'react';
import { Button } from '../../ui/button';
import { Volume2 } from 'lucide-react';
import { Slider } from '../../ui/slider';
import { MetronomeIcon } from './MetronomeIcon';
import { DropdownPortal } from '../../ui/dropdown-portal';

export const MetronomeToggle = ({
  metronomeEnabled,
  setMetronomeEnabled,
  metronomeVolume,
  setMetronomeVolume,
  metronomeGainRef,
  currentBeat = 0,
}) => {
  const [showVolume, setShowVolume] = useState(false);
  const triggerRef = useRef(null);

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        variant={metronomeEnabled ? 'default' : 'ghost'}
        size="sm"
        onClick={() => setMetronomeEnabled(!metronomeEnabled)}
        onMouseEnter={() => metronomeEnabled && setShowVolume(true)}
        onMouseLeave={() => setShowVolume(false)}
        className={`relative p-2 ${metronomeEnabled ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/50' : 'text-zinc-400 hover:text-white'}`}
        data-testid="metronome-toggle"
        title={metronomeEnabled ? `Metronome On (${Math.round(metronomeVolume * 100)}%)` : 'Metronome Off'}
      >
        <MetronomeIcon className="w-5 h-5" />
        {metronomeEnabled && currentBeat > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
            {currentBeat}
          </span>
        )}
      </Button>
      {/* Metronome Volume Slider (portal to avoid clip) */}
      <DropdownPortal
        triggerRef={triggerRef}
        isOpen={showVolume}
        onClose={() => setShowVolume(false)}
        align="center"
        sideOffset={4}
      >
        <div
          className="bg-zinc-900 border border-zinc-700 rounded-lg p-2 shadow-xl min-w-[120px]"
          onMouseEnter={() => setShowVolume(true)}
          onMouseLeave={() => setShowVolume(false)}
        >
          <div className="flex items-center gap-2">
            <Volume2 className="w-3 h-3 text-zinc-400" />
            <Slider
              value={[metronomeVolume * 100]}
              onValueChange={([v]) => {
                setMetronomeVolume(v / 100);
                if (metronomeGainRef?.current) {
                  metronomeGainRef.current.gain.value = v / 100;
                }
              }}
              max={100}
              step={5}
              className="flex-1"
              data-testid="metronome-volume-slider"
            />
            <span className="text-[10px] text-zinc-400 w-6">{Math.round(metronomeVolume * 100)}%</span>
          </div>
        </div>
      </DropdownPortal>
    </div>
  );
};

export default MetronomeToggle;
